import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SpinnerGap, Handshake, DownloadSimple, ArrowLeft, MagnifyingGlass } from '@phosphor-icons/react';
import { ExportPreviewModal } from '../components/ExportPreviewModal';
import { fmtINR, fmtUSD } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SupportedPlacementsPage = () => {
  const navigate = useNavigate();
  const [allPlacements, setAllPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/placements/supported`, { withCredentials: true })
      .then(res => setAllPlacements(Array.isArray(res.data) ? res.data : []))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const filteredPlacements = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allPlacements;
    return allPlacements.filter((p) =>
      p.candidate_name?.toLowerCase().includes(q) ||
      p.created_by_name?.toLowerCase().includes(q) ||
      p.support_user_name?.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q) ||
      p.module?.toLowerCase().includes(q) ||
      p.status?.toLowerCase().includes(q) ||
      p.team_name?.toLowerCase().includes(q)
    );
  }, [allPlacements, searchQuery]);

  const totalAmount = filteredPlacements.reduce((s, p) => s + (p.my_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="supported-placements-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9" data-testid="back-btn"><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Supported Placements</h1>
            <p className="text-slate-500 mt-1">
              {filteredPlacements.length} placement{filteredPlacements.length !== 1 ? 's' : ''} shown
              {searchQuery.trim() ? ` matching "${searchQuery.trim()}"` : ' — split or sourcing support'}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => setShowExport(true)} className="gap-1.5" data-testid="export-supported-btn">
          <DownloadSimple size={16} /> Export to Excel
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-2xl font-bold text-blue-700">{filteredPlacements.length}</p>
          <p className="text-sm text-slate-500">Total Supported</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-lg font-bold text-emerald-700">{fmtINR(totalAmount)}</p>
          <p className="text-sm text-slate-500">My Total Earnings</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-2xl font-bold text-slate-700">{filteredPlacements.filter(p => p.my_role === 'sourcing_support').length}</p>
          <p className="text-sm text-slate-500">As Sourcing Support</p>
        </CardContent></Card>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search candidate, added by, support partner, category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9"
              data-testid="supported-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><SpinnerGap size={32} className="animate-spin text-blue-600 mx-auto" /></div>
          ) : filteredPlacements.length === 0 ? (
            <div className="p-12 text-center">
              <Handshake size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">{searchQuery.trim() ? 'No placements match your search' : 'No supported placements yet'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {['Candidate', 'Category', 'Module', 'Status', 'My Role', 'Split %', 'Bill Rate', 'Pay Rate', 'Margin', 'My Share', 'Added By', 'Start Date'].map(h => (
                      <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredPlacements.map((p, idx) => {
                    const placementId = p.id || p._id || '';
                    return (
                    <tr key={placementId || idx} className="hover:bg-slate-50">
                      <td className="px-3 py-3">
                        {placementId ? (
                          <Link to={`/dashboard/placements/${placementId}`} className="text-blue-600 hover:underline font-medium">{p.candidate_name}</Link>
                        ) : (
                          <span className="font-medium text-slate-800">{p.candidate_name}</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs">{p.category?.replace(/_/g, ' ')}</td>
                      <td className="px-3 py-3"><Badge variant="outline" className="text-[10px]">{p.module}</Badge></td>
                      <td className="px-3 py-3">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${p.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{p.status?.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-3 py-3">
                        <Badge className={p.my_role === 'sourcing_support' ? 'bg-purple-100 text-purple-700 text-[10px]' : 'bg-blue-100 text-blue-700 text-[10px]'}>
                          {p.my_role === 'sourcing_support' ? 'Sourcing' : 'Split'}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-xs font-medium">{p.my_role === 'split' && p.my_percentage ? `${p.my_percentage}%` : '-'}</td>
                      <td className="px-3 py-3 text-xs">{fmtUSD(p.bill_rate)}</td>
                      <td className="px-3 py-3 text-xs">{fmtUSD(p.pay_rate)}</td>
                      <td className="px-3 py-3 font-medium text-xs">{fmtUSD(p.margin)}</td>
                      <td className="px-3 py-3 font-bold text-emerald-700 text-xs">{fmtINR(p.my_amount)}</td>
                      <td className="px-3 py-3 text-slate-600 text-xs">{p.created_by_name}</td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap">{p.start_date || '-'}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ExportPreviewModal open={showExport} onClose={() => setShowExport(false)}
        data={filteredPlacements} fileName={`Supported_Placements_${new Date().toISOString().slice(0,10)}`}
        title="Supported Placements Export Preview"
        columns={[
          { key: 'candidate', header: 'Candidate', accessor: r => r.candidate_name },
          { key: 'category', header: 'Category', accessor: r => r.category?.replace(/_/g, ' ') },
          { key: 'module', header: 'Module', accessor: r => r.module },
          { key: 'status', header: 'Status', accessor: r => r.status },
          { key: 'role', header: 'My Role', accessor: r => r.my_role },
          { key: 'bill', header: 'Bill Rate ($)', accessor: r => r.bill_rate },
          { key: 'pay', header: 'Pay Rate ($)', accessor: r => r.pay_rate },
          { key: 'margin', header: 'Margin ($)', accessor: r => (r.margin || 0).toFixed(2) },
          { key: 'share', header: 'My Share (INR)', accessor: r => r.my_amount || 0 },
        ]}
      />
    </div>
  );
};

export default SupportedPlacementsPage;
